<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Dropdown from 'dashboard/components-next/wgpt/Dropdown/Dropdown.vue';
import { stageFill } from '../../helpers/stageFill';

const props = defineProps({
  board: {
    type: Object,
    required: true,
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  currentUser: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['click', 'edit']);

const { t } = useI18n();

const boardMenuItems = [
  {
    label: t('WGPT_BOARDS.OVERVIEW.CONTEXT_MENU.SETTINGS'),
    action: 'edit',
    value: 'edit',
    icon: 'i-lucide-settings',
  },
];

const hasDescription = computed(() => {
  return props.board.description.length > 0;
});

const description = computed(() => {
  return hasDescription.value ? props.board.description : '';
});

// Each stage's share of the cards in its colour, with its name and count on hover; an empty one is a short grey stub
const EMPTY_SHARE = 0.4;

const stages = computed(() =>
  (props.board.lists || []).map((list, index) => ({
    id: list.id,
    fill:
      list.cards_count === 0 ? 'bg-n-slate-4' : stageFill(index, list.archive),
    share: Math.max(list.cards_count, EMPTY_SHARE),
    tooltip: t(
      'WGPT_BOARDS.OVERVIEW.CARD.STAGE_TOOLTIP',
      { name: list.name, count: list.cards_count },
      list.cards_count
    ),
  }))
);

const counts = computed(() => {
  const cards = (props.board.lists || []).reduce(
    (sum, list) => sum + list.cards_count,
    0
  );
  return [
    t('WGPT_BOARDS.OVERVIEW.CARD.CARDS_COUNT', cards),
    t('WGPT_BOARDS.OVERVIEW.CARD.STAGES_COUNT', stages.value.length),
  ].join(' · ');
});

const hasMinimalAccess = computed(() => {
  const authorization = props.board.config?.authorization || {};
  if (props.isAdmin) return true;

  const managers = authorization?.managers || [];
  if (managers.includes(props.currentUser.id)) return true;

  const operators = authorization?.operators || [];
  if (operators.includes(props.currentUser.id)) return true;

  const readers = authorization?.readers || [];
  if (readers.includes(props.currentUser.id)) return true;

  return false;
});

const handleClick = () => {
  emit('click');
};

const handleAction = ({ action }, toggle) => {
  emit('edit', action);
  toggle();
};
</script>

<template>
  <!-- The whole card opens the board -->
  <CardLayout
    role="link"
    tabindex="0"
    class="cursor-pointer hover:bg-n-alpha-1 focus-visible:outline-2 focus-visible:outline-n-brand"
    @click="handleClick"
    @keydown.enter.self="handleClick"
  >
    <div class="flex gap-2">
      <div class="flex justify-between w-full gap-2">
        <div class="flex items-center justify-start w-full min-w-0 gap-2">
          <span
            class="text-base truncate group-hover/cardLayout:underline underline-offset-2 group-hover/cardLayout:text-n-blue-text text-n-slate-12"
          >
            {{ board.name }}
          </span>
        </div>
        <!-- The settings menu doesn't open the board -->
        <Dropdown @click.stop>
          <template #trigger="{ toggle }">
            <Button
              v-if="hasMinimalAccess"
              icon="i-lucide-ellipsis-vertical"
              color="slate"
              size="xs"
              variant="ghost"
              class="rounded-md group-hover:bg-n-alpha-2"
              @click="toggle"
            />
          </template>
          <template #default="{ toggle }">
            <DropdownMenu
              class="!static"
              :menu-items="boardMenuItems"
              @action="e => handleAction(e, toggle)"
            />
          </template>
        </Dropdown>
      </div>
    </div>
    <span
      class="text-sm line-clamp-2 min-h-[40px]"
      :class="
        hasDescription
          ? 'text-slate-500 dark:text-slate-400'
          : 'text-slate-400 dark:text-slate-700'
      "
    >
      {{ description }}
    </span>
    <template v-if="board.lists">
      <div class="flex h-2 gap-1" aria-hidden="true">
        <div v-if="!stages.length" class="flex-1 rounded-full bg-n-slate-4" />
        <div
          v-for="stage in stages"
          :key="stage.id"
          v-tooltip.top="stage.tooltip"
          class="rounded-full"
          :class="stage.fill"
          :style="{ flex: stage.share }"
        />
      </div>
      <span class="text-sm text-n-slate-11">{{ counts }}</span>
    </template>
  </CardLayout>
</template>
